import { ChevronDown, NotebookPen, ShieldAlert, ShieldCheck } from 'lucide-react'
import SteamIcon from '@/components/site/SteamIcon'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { positionLabel, type MemberPosition } from '@/lib/myPositions'
import { memberSteamProfileUrl } from '@/lib/steam'
import { useLanguage } from '@/contexts/LanguageContext'

export type { MemberPosition }

export interface Member {
  id: string
  display_name: string
  username: string
  avatar_url: string
  team: 'TLL' | 'TLLA'
  // What they want to play, in Settings order (backend/members/positions.py).
  positions?: MemberPosition[]
  steam_linked?: boolean
  steam_name?: string
  // Staff only: /api/members leaves it out for everyone else.
  steam_id?: string
  steam_verified?: boolean
  // Staff only, like steam_id: how many staff notes this member has.
  note_count?: number
  // WARDOGS time from Steam (backend/members/steam_playtime.py): lifetime and
  // the last two weeks, in minutes, exactly as Steam reports them. Absent when
  // Steam is not linked or not read yet.
  wardogs_playtime?: WardogsPlaytime | null
  // When they entered the clan, epoch seconds; 0 = unknown (predates the table).
  clan_joined_at?: number
}

export type WardogsPlaytime =
  | { total: number; two_weeks: number }
  | { private: true }

type Translations = ReturnType<typeof useLanguage>['T']

/** Dakika → "4,5 sa" / "12 sa" / "25 dk", ondalık yalnızca 10 saatin altında. */
export function formatPlaytime(minutes: number, T: Translations, lang: string): string {
  if (minutes < 60) return T.members.playtimeMinutes.replace('{n}', String(minutes))
  const h = minutes / 60
  const n = h < 10
    ? h.toLocaleString(lang, { maximumFractionDigits: 1 })
    : String(Math.round(h))
  return T.members.playtimeHours.replace('{n}', n)
}

function formatLastSeen(ts: number, T: Translations): string {
  if (!ts) return T.members.never
  const diff = Math.floor(Date.now() / 1000 - ts)
  if (diff < 60) return T.members.justNow
  if (diff < 3600) return T.members.mAgo.replace('{n}', String(Math.floor(diff / 60)))
  if (diff < 86400) return T.members.hAgo.replace('{n}', String(Math.floor(diff / 3600)))
  if (diff < 86400 * 30) return T.members.dAgo.replace('{n}', String(Math.floor(diff / 86400)))
  return T.members.moAgo.replace('{n}', String(Math.floor(diff / 86400 / 30)))
}

const badge = 'shrink-0 rounded-sm border px-1 py-px text-[9px] font-bold uppercase tracking-[0.1em]'

const ROLE_BADGE_COLORS: Record<string, string> = {
  owner:      'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
  admin:      'border-purple-500/40 bg-purple-500/10 text-purple-300',
  moderator:  'border-blue-500/40 bg-blue-500/10 text-blue-300',
  strategist: 'border-teal-400/40 bg-teal-400/10 text-teal-300',
  member:     'border-white/15 bg-white/5 text-muted-foreground',
}

/** Satırdaki pozisyon: simgesi, simgesi yoksa adının başı. Tam ad ipucunda. */
function PositionMark({ position }: { position: MemberPosition }) {
  const label = positionLabel(position)
  if (position.emoji) return <EmojiGlyph emoji={position.emoji} size={16} title={label} />
  return (
    <span title={label} className="rounded-sm border border-white/15 px-0.5 text-[9px] leading-[14px] text-muted-foreground">
      {position.name.slice(0, 3)}
    </span>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground/60">{label}</dt>
      <dd className="mt-0.5 text-xs text-foreground/85">{children}</dd>
    </div>
  )
}

interface MemberRowProps {
  member: Member
  lastSeen: number
  isOnline: boolean
  // Dashboard role, resolved from the member's Discord roles. Display only:
  // it is granted and revoked in Discord, never from this page.
  canSeeRoles: boolean
  memberRole: string
  // The SteamID64 is what the game server and the reserved slots key on, so only
  // staff see it. Members see the account name, which is what identifies it.
  canSeeSteamId: boolean
  open: boolean
  onToggle: () => void
  // Staff: opens the notes + manual SteamID panel. Omitted for everyone else.
  onManage?: () => void
}

export default function MemberRow({
  member, lastSeen, isOnline, canSeeRoles, memberRole, canSeeSteamId, open, onToggle, onManage,
}: MemberRowProps) {
  const { T, lang } = useLanguage()
  const playtime = member.wardogs_playtime
  const positions = member.positions ?? []
  const lastSeenLabel = formatLastSeen(lastSeen, T)

  // The account name is the door to the profile for everyone. The id still goes
  // only to staff, so the address is built by the backend: /api/members/<id>/steam
  // bounces to Steam.
  const steamLink = (className: string) => (
    <a
      href={memberSteamProfileUrl(member.id)}
      target="_blank"
      rel="noreferrer"
      title={T.members.steamOpenProfile}
      className={`truncate underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground ${className}`}
    >
      {member.steam_name || T.members.steamNoName}
    </a>
  )

  return (
    <div
      // Satırın tamamı açar; içindeki bağlantı ve düğmeler kendi işini yapar.
      // Klavye ve ekran okuyucu için asıl denetim sondaki ok düğmesidir.
      onClick={e => { if (!(e.target as HTMLElement).closest('a, button, dl')) onToggle() }}
      data-member-row={member.id}
      className={`relative cursor-pointer border-b border-white/[0.06] px-2 py-1.5 transition-colors hover:bg-white/[0.04] ${open ? 'z-20 bg-white/[0.04]' : ''}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="relative shrink-0">
          <img
            src={member.avatar_url}
            alt=""
            loading="lazy"
            className="h-8 w-8 rounded-full object-cover ring-1 ring-white/15"
            onError={(e) => {
              ;(e.target as HTMLImageElement).src =
                'https://cdn.discordapp.com/embed/avatars/0.png'
            }}
          />
          {isOnline && (
            <span className="absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-[#0b0b0d] bg-emerald-400" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {/* Discord adı veriden gelir, büyütülmez (bkz. i18n bölümü). */}
            <span title={`@${member.username}`} className="truncate text-[13px] font-semibold leading-5">
              {member.display_name}
            </span>
            {canSeeRoles && memberRole !== 'member' && (
              <span className={`${badge} ${ROLE_BADGE_COLORS[memberRole] ?? ROLE_BADGE_COLORS.member}`}>
                {T.roles[memberRole as keyof typeof T.roles] ?? memberRole}
              </span>
            )}
            {positions.length > 0 && (
              <span className="ml-auto flex shrink-0 items-center gap-1 pl-1">
                {positions.map(p => <PositionMark key={p.id} position={p} />)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground/70">
            <span
              title={T.members.lastSeen.replace('{t}', lastSeenLabel)}
              className={`shrink-0 ${isOnline ? 'text-emerald-400' : ''}`}
            >
              {isOnline ? T.members.onlineShort : lastSeenLabel}
            </span>
            <span aria-hidden className="text-muted-foreground/30">·</span>
            {member.steam_linked ? (
              <>
                {steamLink('min-w-0')}
                {member.steam_verified
                  ? <ShieldCheck className="h-3 w-3 shrink-0 text-primary" aria-label={T.members.steamVerified} />
                  : (
                    <span title={T.members.steamUnverified} className="shrink-0 text-amber-400">
                      <ShieldAlert className="h-3 w-3" aria-label={T.members.steamUnverified} />
                    </span>
                  )}
              </>
            ) : (
              <span className="truncate text-muted-foreground/40">{T.members.steamNotLinked}</span>
            )}
            {/* Gizli profil 0 değildir: Steam bize hiçbir şey söylemedi. */}
            {playtime && (
              'private' in playtime ? (
                <span title={T.members.playtimePrivateHint} className="ml-auto shrink-0 pl-1 text-muted-foreground/50">
                  {T.members.playtimePrivate}
                </span>
              ) : (
                <span className="ml-auto shrink-0 pl-1 tabular-nums">
                  <span title={T.members.playtimeTotalHint} className={playtime.total ? 'text-foreground/80' : ''}>
                    {formatPlaytime(playtime.total, T, lang)}
                  </span>
                  <span aria-hidden className="text-muted-foreground/30"> · </span>
                  <span title={T.members.playtimeFortnightHint} className={playtime.two_weeks ? 'text-foreground/80' : ''}>
                    <span className="text-muted-foreground/60">{T.members.playtimeFortnight} </span>
                    {formatPlaytime(playtime.two_weeks, T, lang)}
                  </span>
                </span>
              )
            )}
          </div>
        </div>

        {onManage && (
          <button
            type="button"
            onClick={onManage}
            title={T.members.manage.open}
            aria-label={T.members.manage.open}
            className={`flex h-7 shrink-0 cursor-pointer items-center gap-0.5 rounded-sm px-1 text-[10px] transition-colors hover:bg-white/10 hover:text-foreground ${
              member.note_count ? 'text-primary' : 'text-muted-foreground/50'
            }`}
          >
            <NotebookPen className="h-3 w-3" />
            {member.note_count ? member.note_count : null}
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          title={open ? T.members.hideDetails : T.members.showDetails}
          aria-label={open ? T.members.hideDetails : T.members.showDetails}
          className="flex h-7 w-5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground/50 transition-colors hover:bg-white/10 hover:text-foreground"
        >
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {open && (
        // Satırın altında yüzer, listeyi itmez: akışta açılsaydı komşu sütunlarda
        // satır boyu boşluk kalırdı.
        <dl className="absolute inset-x-0 top-full grid cursor-default grid-cols-2 gap-x-3 gap-y-2 rounded-b-sm border border-white/15 bg-[#121215] p-3 shadow-xl shadow-black/60">
          <div className="col-span-2">
            <Detail label={T.members.positions.label}>
              {positions.length > 0 ? (
                <span className="flex flex-col gap-1">
                  {positions.map(p => (
                    <span key={p.id} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <EmojiGlyph emoji={p.emoji} size={14} title={p.name} />
                      {p.name}
                      {p.specs.length > 0 && <span aria-hidden className="text-muted-foreground/40">·</span>}
                      {p.specs.map(spec => (
                        <span key={spec.name} className="flex items-center gap-1 text-muted-foreground">
                          <EmojiGlyph emoji={spec.emoji} size={12} title={spec.name} />
                          {spec.name}
                        </span>
                      ))}
                    </span>
                  ))}
                </span>
              ) : <span className="text-muted-foreground/60">{T.members.positions.none}</span>}
            </Detail>
          </div>
          <Detail label={T.members.detailDiscord}><span className="block truncate">@{member.username}</span></Detail>
          <Detail label={T.members.detailLastSeen}>{isOnline ? T.members.online : lastSeenLabel}</Detail>
          <Detail label={T.members.detailJoined}>
            {member.clan_joined_at ? new Date(member.clan_joined_at * 1000).toLocaleDateString(lang) : '-'}
          </Detail>
          <Detail label={T.members.detailPlaytime}>
            {!playtime ? '-' : 'private' in playtime ? T.members.playtimePrivate : (
              <>
                {T.members.playtimeTotal} {formatPlaytime(playtime.total, T, lang)}
                {' · '}
                {T.members.playtimeFortnight} {formatPlaytime(playtime.two_weeks, T, lang)}
              </>
            )}
          </Detail>
          <div className="col-span-2">
            <Detail label={T.members.detailSteam}>
              {member.steam_linked ? (
                <span className="flex items-center gap-1.5">
                  <SteamIcon size={12} />
                  {steamLink('min-w-0 text-foreground/85')}
                  {member.steam_verified ? (
                    <ShieldCheck className="h-3 w-3 shrink-0 text-primary" aria-label={T.members.steamVerified} />
                  ) : (
                    <span className={`${badge} border-amber-500/40 bg-amber-500/10 text-amber-400`}>
                      {T.members.steamUnverified}
                    </span>
                  )}
                </span>
              ) : <span className="text-muted-foreground/60">{T.members.steamNotLinked}</span>}
              {canSeeSteamId && member.steam_id && (
                <span className="mt-0.5 block select-all font-mono text-[10px] tracking-tight text-muted-foreground/60">
                  {member.steam_id}
                </span>
              )}
            </Detail>
          </div>
        </dl>
      )}
    </div>
  )
}
